import { useEffect, useState } from "react";
import { Hourglass, ShieldCheck } from "lucide-react";
import { useApp } from "@/context/AppContext";

import { usd as money } from "@/lib/purchases";
const pad = (n) => String(n).padStart(2, "0");
const clock = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
};

const useNow = (active) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  return now;
};

export const ReservationNotice = ({ slug, reservation }) => {
  const { priceFor } = useApp();
  const ticking = !!reservation && !reservation.never_expires && !reservation.expired;
  const now = useNow(ticking);
  if (!reservation) return null;
  const pb = (key) => priceFor(key);
  if (reservation.never_expires) {
    return (
      <p className="flex items-start gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/[0.06] p-3 text-xs text-emerald-100" data-testid="reservation-permanent">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        {reservation.reason === "reference" ? "Reference case study preview — always available." : "Your live test preview is permanently reserved in your workspace."}
      </p>
    );
  }
  const expiresMs = new Date(reservation.expires_at).getTime() - now;
  const inBest = now < new Date(reservation.best_until).getTime();
  const expired = reservation.expired || expiresMs <= 0;
  if (expired) {
    return (
      <p className="rounded-xl border border-rose-400/30 bg-rose-500/[0.06] p-3 text-xs text-rose-100" data-testid="reservation-expired">
        Your 72-hour preview reservation has ended. Your 20-Criteria Scorecard stays in your workspace, and the PDF report ({money(pb("PDF_REPORT"))}) is available any time.
      </p>
    );
  }
  return (
    <div className={`space-y-2 rounded-xl border p-3 text-xs leading-relaxed ${inBest && !expired ? "border-cyan-400/50 bg-cyan-500/[0.07] shadow-[0_0_24px_rgba(34,211,238,0.12)]" : "border-white/10 bg-white/[0.03]"}`}
      data-testid="reservation-notice" data-best-window={inBest && !expired ? "true" : "false"}>
      <p className="text-slate-200" data-testid="reservation-text">
        Your live test preview ({slug} preview) and 20-Criteria Scorecard are reserved in your workspace for 72 hours. Best results come from acting in the first 24 hours — download your PDF report ({money(pb("PDF_REPORT"))}) or claim your Custom Domain Launch ({money(pb("DFY_WEBSITE_LAUNCH"))}).
      </p>
      <p className="flex flex-wrap items-center gap-2 font-semibold">
        <Hourglass className={`h-3.5 w-3.5 ${inBest ? "text-cyan-300" : "text-slate-400"}`} aria-hidden="true" />
        {(
          <>
            <span className="font-mono text-white" data-testid="reservation-countdown">{clock(expiresMs)}</span>
            <span className="text-slate-400">left</span>
            {inBest && <span className="rounded-full bg-cyan-400/15 px-2 py-0.5 text-[10px] text-cyan-200" data-testid="reservation-best-badge">Best-results window</span>}
          </>
        )}
      </p>
    </div>
  );
};
